import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/app/components/ui/alert-dialog';
import { Button } from '@/app/components/ui/button';
import { useFetch } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { SubscriptionProductType } from '@/app/types';
import { reportEvent } from '@/app/utils';
import { subscriptionsCancelSubscriptionChange } from '../apis';

export const CancelNextPlan = ({
  children,
  product,
}: {
  children: React.ReactNode;
  product: SubscriptionProductType;
}) => {
  const t = useTranslations();
  const subscription = useStore((state) => state.subscription);
  const { updateRefreshGetProducts } = useStore((state) => ({
    updateRefreshGetProducts: state.updateRefreshGetProducts,
  }));
  const [open, setOpen] = useState(false);
  const [txt, setTxt] = useState(t('payments.cancelChangeAlert.description'));
  const [status, setStatus] = useState<'unspecified' | 'success' | 'error'>('unspecified');

  useEffect(() => {
    // 初始化状态
    if (open) {
      const { product_type, billing_period, plan_type } = product;
      setStatus('unspecified');
      setTxt(t('payments.cancelChangeAlert.description'));
      reportEvent('vip_change_exposure', {
        change_type: 'cancel change',
        subscribe_type: `${product_type}-${billing_period}`,
        vip_type: plan_type,
        subscribe_type_origin: `${subscription?.product_type}-${subscription?.billing_period}`,
        vip_type_original: subscription?.plan_type || '',
      });
    }
  }, [open]);

  const closeCancelChange = () => {
    setOpen(false);
    updateRefreshGetProducts(status === 'success');
  };

  const handleCancel = () => {
    const { product_type, billing_period, plan_type, sub_change_id = '' } = product;
    setStatus('unspecified');
    runCancelChange(sub_change_id);
    // 埋点
    reportEvent('vip_change_confirm', {
      change_type: 'cancel change',
      subscribe_type: `${product_type}-${billing_period}`,
      vip_type: plan_type,
      subscribe_type_origin: `${subscription?.product_type}-${subscription?.billing_period}`,
      vip_type_original: subscription?.plan_type || '',
    });
  };

  const { loading, run: runCancelChange } = useFetch(
    (sub_change_id: string) =>
      subscriptionsCancelSubscriptionChange({
        body: {
          subscription_change_id: sub_change_id,
        },
      }),
    {
      manual: true,
      onSuccess: ({ data }) => {
        if (data && data.status === 'canceled') {
          setTxt(t('payments.cancelChangeAlert.success'));
          setStatus('success');
          return;
        }
        setTxt(t('payments.cancelChangeAlert.error'));
        setStatus('error');
        return;
      },
    },
  );

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>{children}</AlertDialogTrigger>
      <AlertDialogContent className="w-[640px] rounded-16 border-none bg-system-bg04 text-left opacity-100 sm:w-full">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-left">{t('payments.cancelChangeAlert.title')}</AlertDialogTitle>
        </AlertDialogHeader>
        <AlertDialogDescription className="text-left">{txt}</AlertDialogDescription>
        <AlertDialogFooter className="flex flex-row justify-end gap-4">
          {loading ? (
            <Button
              variant="outline"
              disabled={loading}
              className="w-full text-sm leading-[2]"
              onClick={closeCancelChange}
            >
              {t('payments.cancelChangeAlert.loadingBtn')}
            </Button>
          ) : status === 'unspecified' ? (
            <>
              <Button
                variant="outline"
                disabled={loading}
                className="w-[120px] text-sm leading-[2]"
                onClick={closeCancelChange}
              >
                {t('payments.cancelChangeAlert.cancelBtn')}
              </Button>
              <Button
                variant="primary"
                disabled={loading}
                className="w-[120px] text-sm leading-[2]"
                onClick={handleCancel}
              >
                {t('payments.cancelChangeAlert.confirmBtn')}
              </Button>
            </>
          ) : (
            <Button
              variant="outline"
              disabled={loading}
              className="w-[120px] text-sm leading-[2]"
              onClick={closeCancelChange}
            >
              {t('payments.cancelChangeAlert.closeBtn')}
            </Button>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
